/**
 * Step-by-step agent setup: register, save the one-time key, install (Linux or Windows server) with the key
 * already filled in, then wait for the agent to come online. Once registered, going back never registers again.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Copy, Loader2 } from 'lucide-react';
import Modal from '@/components/common/Modal';
import { useAgents, useCreateAgent } from '@/hooks/useAgents';
import type { AgentCreateResponse } from '@/types/agent';
import { apiError } from '@/utils/apiError';
import {
  CodeBlock, DownloadPackageButton, buildCommands, composeFile, dockerEngineStep, startCommands, windowsVmStep,
} from '@/components/agents/agentSetup';

const CONNECTION_LABELS: Record<string, string> = { long_poll: 'Long Poll', websocket: 'WebSocket' };
const STEPS = ['Register', 'Save key', 'Install', 'Connect'];

export default function AddAgentWizard({ allowedConnectionTypes, agentVersion, platformUrl, onClose }: {
  allowedConnectionTypes: string[];
  agentVersion: string;
  platformUrl: string;
  onClose: () => void;
}) {
  const createMutation = useCreateAgent();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [connectionType, setConnectionType] = useState(allowedConnectionTypes[0] ?? 'long_poll');
  const [agent, setAgent] = useState<AgentCreateResponse | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [server, setServer] = useState<'linux' | 'windows'>('linux');
  const [confirmClose, setConfirmClose] = useState(false);
  // Refresh the agent list while waiting for the new agent to check in.
  const { data: agents = [] } = useAgents({ refetchInterval: step === 3 ? 5000 : false });
  const live = agents.find((a) => a.id === agent?.id);
  const online = live?.status === 'online' || live?.status === 'scanning';

  function requestClose() {
    if (agent && !saved) setConfirmClose(true);
    else onClose();
  }

  function register(event: React.FormEvent) {
    event.preventDefault(); setError('');
    createMutation.mutate({ name: name.trim(), connection_type: connectionType }, {
      onSuccess: (response) => { setAgent(response); setStep(1); },
      onError: (failure) => setError(apiError(failure)),
    });
  }

  async function copyKey() {
    if (!agent) return;
    await navigator.clipboard.writeText(agent.api_key);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal isOpen onClose={requestClose} title="Add agent" size="xl">
      <ol className="mb-5 flex flex-wrap gap-2 text-xs" aria-label="Setup steps">
        {STEPS.map((label, index) => (
          <li key={label} aria-current={index === step ? 'step' : undefined}
            className={`rounded-full px-3 py-1 font-medium ${index === step ? 'bg-indigo-600 text-white' : index < step ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      {confirmClose && (
        <div role="alert" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p><strong>The API key won't be shown again.</strong> If you close now without saving it, you'll need to register the agent again.</p>
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-secondary" onClick={() => setConfirmClose(false)}>Keep setting up</button>
            <button type="button" className="btn-primary" onClick={onClose}>Close anyway</button>
          </div>
        </div>
      )}

      {step === 0 && (
        <form onSubmit={register} className="space-y-4">
          <p className="text-sm text-gray-600">Agents run inside your network and scan devices the cloud can't reach. They make outbound-only HTTPS connections, so no inbound firewall rules are needed.</p>
          {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">Agent name
              <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Office network agent" required />
            </label>
            <label className="block text-sm font-medium text-gray-700">Connection type
              <select className="input-field mt-1" value={connectionType} onChange={(e) => setConnectionType(e.target.value)}>
                {allowedConnectionTypes.map((type) => <option key={type} value={type}>{CONNECTION_LABELS[type] ?? type}</option>)}
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={createMutation.isPending || !name.trim()}>{createMutation.isPending ? 'Registering…' : 'Register agent'}</button>
          </div>
        </form>
      )}

      {step === 1 && agent && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700"><strong>{agent.name}</strong> is registered. Copy its API key and store it somewhere safe. <strong>It is shown only once.</strong> The next step fills it into the install commands for you.</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded border border-amber-200 bg-amber-50 px-3 py-2 font-mono text-sm text-amber-900">{agent.api_key}</code>
            <button type="button" onClick={copyKey} className="btn-secondary flex items-center gap-1"><Copy className="h-4 w-4" />{copied ? 'Copied' : 'Copy key'}</button>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />I've saved this key
          </label>
          <div className="flex justify-end"><button type="button" className="btn-primary" disabled={!saved} onClick={() => setStep(2)}>Next</button></div>
        </div>
      )}

      {step === 2 && agent && (
        <div className="space-y-4 text-sm">
          <fieldset className="flex flex-wrap gap-4 text-gray-700">
            <legend className="mb-1 font-semibold">Where will the agent run?</legend>
            <label className="flex items-center gap-1"><input type="radio" name="agent-server" checked={server === 'linux'} onChange={() => setServer('linux')} />Linux server</label>
            <label className="flex items-center gap-1"><input type="radio" name="agent-server" checked={server === 'windows'} onChange={() => setServer('windows')} />Windows server</label>
          </fieldset>

          {server === 'windows' && (
            <section aria-label="Windows server preparation">
              <h4 className="font-semibold text-gray-800">Create a small Linux VM on the Windows server</h4>
              <p className="text-gray-600">The agent runs on Linux. Hyper-V hosts an Ubuntu VM connected directly to your network, so scans keep full device detail. If the Windows server is itself a VM (VMware, Azure, AWS), create the Ubuntu VM on that platform instead. See the <Link className="text-indigo-600 underline" to="/docs#agents-windows">Windows server guide</Link>.</p>
              <CodeBlock>{windowsVmStep}</CodeBlock>
              <p className="mt-2 text-gray-600">Open the VM from Hyper-V Manager (Connect), install Ubuntu Server with the defaults, then:</p>
              <CodeBlock>{dockerEngineStep}</CodeBlock>
              <p className="mt-2 font-medium text-indigo-700">Run the remaining steps inside the Ubuntu VM.</p>
            </section>
          )}

          <section aria-label="Install the agent" className="space-y-2">
            <h4 className="font-semibold text-gray-800">{server === 'windows' ? 'Install the agent in the VM' : 'Install the agent'}</h4>
            <p className="text-gray-600">{server === 'linux' && 'Docker must be installed on the server. '}Download the package, copy it to the server, then build it:</p>
            <DownloadPackageButton agentVersion={agentVersion} />
            <CodeBlock>{buildCommands(agentVersion)}</CodeBlock>
            <p className="text-gray-600">Save this as <code>docker-compose.yml</code> in the same folder. Your key is already filled in:</p>
            <CodeBlock>{composeFile(platformUrl, agentVersion, agent.api_key, agent.connection_type)}</CodeBlock>
            <p className="text-gray-600">Start the agent:</p>
            <CodeBlock>{startCommands()}</CodeBlock>
          </section>

          <div className="flex justify-between">
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="btn-primary" onClick={() => setStep(3)}>Next</button>
          </div>
        </div>
      )}

      {step === 3 && agent && (
        <div className="space-y-4 text-sm">
          {online ? (
            <p role="status" className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-green-800"><CheckCircle2 className="h-5 w-5" />{agent.name} is online{live?.version ? ` (agent ${live.version})` : ''}. You can now select it when creating a scan.</p>
          ) : (
            <p role="status" className="flex items-center gap-2 rounded-md bg-gray-50 p-3 text-gray-700"><Loader2 className="h-5 w-5 animate-spin" />Waiting for {agent.name} to come online… This usually takes under a minute after <code>docker compose up -d</code>. If it doesn't appear, check the agent's logs.</p>
          )}
          <div className="flex justify-between">
            <button type="button" className="btn-secondary" onClick={() => setStep(2)}>Back</button>
            <button type="button" className="btn-primary" onClick={onClose}>{online ? 'Done' : 'Finish later'}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
